import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Icon, message } from 'antd';
import useClickOutsideHandler from '../../../../ComplexDocumentDesign/HandleClickOutside';
import { SearchSelectContainer } from '../../../../../components/ReusableComponents/Styles';
import { proceedProof } from '../../../../../utils/services/statusMonitoringAPI';
import { BATCH_JOBS_DASHBOARD } from '../translationValues';
import { useTranslation } from 'react-i18next';

const NAMESPACE = ['BATCH_JOBS_DASHBOARD'];

const dummyCallback = () => { };

const debounce = (func, delay) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
};

const ProofSearchSelect = ({
    setNoSearchResult,
    setProffDataList,
    setTotalProofCount,
    setPdfUrl,
    setIsLoading,
    selectedChannel,
    getEmailPreview,
    getPreview,
    keyDataSet = [],
    valueDataSet = {},
    isDynamicKeyValue = false,
    showSelectedKeys = false,
    placeholder = 'Search key: value',
    selectedFilters = [],
    selectedStatsFilter = [],
    setSelectedFilters = dummyCallback,
    chartCallBack = undefined,
    statsKeys = [],
    allowValuesforKeys = [],
    fullWidth = false,
    setStatsFilter = dummyCallback,
    ...props
}) => {
    const { batchId, format } = props;
    const [searchText, setSearchText] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [currentKey, setCurrentKey] = useState(null);
    const [valueOptions, setValueOptions] = useState([]);
    const [openDropDown, setOpenDropDown] = useState(false);
    const [searchSelectFocus, setSearchSelectFocus] = useState(false);

    const optionsListRef = useRef(null);
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);
    const allselectedFilters = [...selectedStatsFilter, ...selectedFilters];

    const { t } = useTranslation(NAMESPACE);

    // Safe filtering of keys
    const filteredKeys = keyDataSet.filter(data => {
        if (!data) return false;
        const key = data.toString().toLowerCase();
        return showSelectedKeys
            ? !allselectedFilters.some(filter => filter.key === key) &&
            (!searchText || key.includes(searchText.toLowerCase()))
            : !searchText || key.includes(searchText.toLowerCase());
    });

    // Helper to get values for a specific key from the provided dataset
    const getValuesBasedOnKey = useCallback(
        searchKey => {
            if (searchKey && valueDataSet) {
                const key = searchKey.charAt(0).toUpperCase() + searchKey.slice(1).toLowerCase();
                const valuesData = valueDataSet[key] || valueDataSet[searchKey] || [];
                return valuesData.map(val => (typeof val === 'string' ? val : val?.key)).filter(Boolean);
            }
            return [];
        },
        [valueDataSet],
    );

    const matchKeyOrValues = (data, keyOrValue) => {
        if (!data || !keyOrValue) return false;
        return data.some(item => item?.toString().toLowerCase() === keyOrValue?.toLowerCase());
    };

    const handleSearchChange = e => {
        const text = e.target.value;
        setSearchText(text);

        if (text.includes(': ')) {
            const parts = text.split(': ');
            const key = parts[0].trim();
            const value = parts[1] ? parts[1].trim() : '';

            setCurrentKey(key);
            if (value && matchKeyOrValues(allowValuesforKeys, key)) {
                const filteredValues = getValuesBasedOnKey(key).filter(val =>
                    val?.toString().toLowerCase().includes(value?.toLowerCase()),
                );
                setValueOptions(filteredValues);
            } else {
                if (isDynamicKeyValue) {
                    fetchStaticValues(key);
                } else {
                    setValueOptions([]);
                }
            }
        } else {
            setCurrentKey(null);
        }
        setHighlightedIndex(-1);
    };

    const fetchStaticValues = useCallback(debounce(query => {
        if (!query) return;
        const key = query.charAt(0).toUpperCase() + query.slice(1);
        const dataSet = valueDataSet[key] || [];
        const values = dataSet.map(val => (typeof val === 'string' ? val : val?.key)).filter(Boolean);
        setValueOptions(values || []);
    }, 300), [valueDataSet]);

    const triggerSearchWithFilters = async (activeFilters = []) => {
        setIsLoading(true);
        const searchArray = [
            { fieldName: 'stage', value: 'yet_to_start' },
            { fieldName: 'channel', value: selectedChannel.toLowerCase()}
        ];

        activeFilters.forEach(f => {
            if (f.key && f.value) {
                searchArray.push({ fieldName: f.key, value: f.value });
            }
        });

        const request = {
            search: searchArray,
            jobId: String(batchId || '')
        };

        try {
            const response = await proceedProof(request);
            if (response?.data && response?.data?.proofs?.length > 0) {
                if (typeof setProffDataList === 'function')
                  setProffDataList(response?.data?.proofs);
                if (typeof setTotalProofCount === 'function')
                  setTotalProofCount({
                    print: response?.data?.printCount,
                    email: response?.data?.emailCount,
                  });

                // Update Preview if we have results
                if (typeof getPreview === 'function' && response?.data?.proofs?.length > 0) {
                    const previewData = response.data?.proofs[0];
                    if (selectedChannel.toLowerCase() === 'print') {
                        if (previewData && previewData?.documentURL) {
                            getPreview(previewData.documentURL, previewData.dataSet);
                        }
                    }
                    else if (selectedChannel.toLowerCase() === 'email'){
                         if (previewData && previewData?.documentURL) {
                            getEmailPreview(previewData.documentURL, previewData.dataSet);
                        }
                    }
                }
            }
            else{
                setPdfUrl();
                setProffDataList();
                setNoSearchResult(true)
                 message.error(t(BATCH_JOBS_DASHBOARD.NO_RESULTS_FOR_SEARCH.key, BATCH_JOBS_DASHBOARD.NO_RESULTS_FOR_SEARCH.value));
            }
        } catch (error) {
            console.error("Error executing search filters:", error);
        }
        finally{
            setIsLoading(false);
        }
    }

    const handleOptionClick = keyOrValue => {
        if (!currentKey) {
            setSearchText(`${keyOrValue}: `);
            setCurrentKey(keyOrValue);
        } else {
            if (keyOrValue) {
                const newFilter = { key: currentKey, value: keyOrValue };
                const nextFilters = [...selectedFilters, newFilter];
                setSelectedFilters(nextFilters);
                triggerSearchWithFilters([...selectedStatsFilter, ...nextFilters]);
                setSearchText('');
                setCurrentKey(null);
                setValueOptions([]);
            }
        }
        setHighlightedIndex(-1);
        if (searchInputRef.current) searchInputRef.current.focus();
    };

    const handleKeyDown = e => {
        const options = currentKey ? valueOptions : filteredKeys;
        if (e.key === 'Enter') {
            if (highlightedIndex >= 0) {
                handleOptionClick(options[highlightedIndex]);
            } else if (isDynamicKeyValue && searchText.includes(': ')) {
                const [_, value] = searchText.split(': ').map(str => str.trim());
                handleOptionClick(value);
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex(prev => Math.min(prev + 1, options.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(prev => Math.max(prev - 1, 0));
        }
    };

    const handleRemoveFilter = filterToRemove => {
        const nextSelected = selectedFilters.filter(
            item => !(item.key === filterToRemove.key && item.value === filterToRemove.value)
        );
        const nextStats = selectedStatsFilter.filter(
            item => !(item.key === filterToRemove.key && item.value === filterToRemove.value)
        );
        setSelectedFilters(nextSelected);
        if (typeof setStatsFilter === 'function') {
            setStatsFilter(nextStats);
        }
        triggerSearchWithFilters([...nextStats, ...nextSelected]);
    };

    const focusSearchInput = () => {
        setSearchSelectFocus(true);
        if (searchInputRef.current) searchInputRef.current.focus();
    };

    const deFocusSearchInput = () => {
        setSearchSelectFocus(false);
        setHighlightedIndex(-1);
        setOpenDropDown(false);
    };

    useClickOutsideHandler({
        ref: containerRef,
        clickInsideHandler: focusSearchInput,
        clickOutsideHandler: deFocusSearchInput
    });

    return (
        <SearchSelectContainer ref={containerRef} fullWidth={fullWidth} tags={allselectedFilters} searchSelectFocus={true}>
            <div className={`search-input-wrapper${searchSelectFocus ? ' highlight' : ''}`}>
                {allselectedFilters.map((filter, index) => (
                    <span className="filter-bubble" key={index}>
                        {filter.key}: {filter.value}
                        <Icon type='close-circle' onClick={() => handleRemoveFilter(filter)} />
                    </span>
                ))}
                <input
                    type="text"
                    className="search-input"
                    value={searchText}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                    placeholder={allselectedFilters.length === 0 ? placeholder : ''}
                    onFocus={() => setOpenDropDown(true)}
                    ref={searchInputRef}
                />
            </div>
            {openDropDown && (
                <ul className="options-list" ref={optionsListRef}>
                    {(currentKey ? valueOptions : filteredKeys).map((value, index) => (
                        <li style={{display:'flex'}}
                            key={index}
                            className={`option ${index === highlightedIndex ? 'highlighted' : ''}`}
                            onClick={() => handleOptionClick(value)}
                            onMouseOver={() => setHighlightedIndex(index)}
                        >
                            {value}
                        </li>
                    ))}
                </ul>
            )}
        </SearchSelectContainer>
    );
};

export default ProofSearchSelect;
